import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "../context/auth-context";
import { parseApiResponse } from "../models";
import { fetchServiceJson } from "./service-fetch";

export async function fetchOrganisationInventoryAccessPoints(profileId, token) {
  if (!profileId) return null;

  const response = await fetchServiceJson(
    `crowdcontrol/organisation/${profileId}/accesspoints/inventory`,
    token,
  );

  return response ? parseApiResponse(response) : null;
}

export const getAccessPointsQueryKey = (orgId, token) => [
  "accessPoints",
  orgId,
  token,
];

export function useFetchAccessPoint({
  orgId,
  token,
  enabled = false,
  ...config
} = {}) {
  const client = useClient();
  return useQuery(
    getAccessPointsQueryKey(orgId, token),
    () => {
      const url = `crowdcontrol/organisation/${orgId}/accesspoints/inventory`;
      const requestConfig = {
        method: "GET",
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      return client(url, requestConfig).then(parseApiResponse);
    },
    {
      enabled: !!enabled && !!orgId,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      ...config,
    },
  );
}

export function useFetchMainAccessPoint({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, token }) => {
      const url = `crowdcontrol/organisation/${orgId}/point/main`;
      const requestConfig = {
        method: "GET",
      };

      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }

      return client(url, requestConfig).then(parseApiResponse);
    },
    { ...config },
  );
}

export function useMarketplaceAccessPoints({
  queryKey,
  limit,
  page,
  search,
  randomSeed,
  token,
  ...config
}) {
  const client = useClient();

  return useQuery(
    queryKey,
    async () => {
      const params = [
        `limit=${limit}`,
        `page=${page}`,
        `searchTerm=${search}`,
      ];
      if (page > 1 && randomSeed) {
        params.push(`randomSeed=${randomSeed}`);
      }

      const response = await client(
        `crowdcontrol/accesspoints/marketplace?${params.join("&")}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return response.data;
    },
    {
      enabled: Boolean(token),
      keepPreviousData: true,
      ...config,
    },
  );
}
